import type { WorkingHours } from "@ai-admin/shared";
import { useTranslation } from "react-i18next";
import { Input, Toggle } from "../../ui/index.js";

/** Неделя с понедельника. Сетка как на странице мастера в кабинете (`features/calendar/MasterPage.tsx`). */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;
const hh = (s: string) => s.slice(0, 5);

export const DEFAULT_HOURS: WorkingHours[] = [1, 2, 3, 4, 5, 6].map((weekday) => ({ weekday, start: "09:00", end: "18:00" }));

export const hoursValid = (grid: WorkingHours[]): boolean => grid.every((h) => /^\d{2}:\d{2}$/.test(hh(h.start)) && hh(h.start) < hh(h.end));

export const normalizeHours = (grid: WorkingHours[]): WorkingHours[] => grid.map((h) => ({ weekday: h.weekday, start: hh(h.start), end: hh(h.end) }));

/** Канонический вид для сравнения снимков: сервер отдаёт график отсортированным, сетка дописывает включённый день в конец. */
export const canonicalHours = (grid: WorkingHours[]): WorkingHours[] =>
  normalizeHours(grid).sort((a, b) => a.weekday - b.weekday || a.start.localeCompare(b.start) || a.end.localeCompare(b.end));

/** Часы работы по дням: выключатель дня и интервалы «с» и «до». */
export function HoursGrid({ grid, onChange }: { grid: WorkingHours[]; onChange: (g: WorkingHours[]) => void }) {
  const { t, i18n } = useTranslation();
  const weekdayName = (wd: number) => new Intl.DateTimeFormat(i18n.language, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, 10, 1 + wd)));
  const rows = (wd: number) => grid.map((h, i) => ({ h, i })).filter((x) => x.h.weekday === wd);
  const setRow = (i: number, patch: Partial<WorkingHours>) => onChange(grid.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  const toggleDay = (wd: number, on: boolean) => onChange(on ? [...grid, { weekday: wd, start: "09:00", end: "18:00" }] : grid.filter((h) => h.weekday !== wd));
  return (
    <div className="space-y-1">
      {WEEK.map((wd) => (
        <div key={wd} className="border-b border-slate-100 py-1 last:border-b-0">
          <Toggle checked={rows(wd).length > 0} onChange={(v) => toggleDay(wd, v)} label={<span className="capitalize">{weekdayName(wd)}</span>} />
          {rows(wd).map(({ h, i }) => (
            <div key={i} className="mt-1 flex items-center gap-2">
              <Input
                type="time"
                step={300}
                value={hh(h.start)}
                onChange={(e) => setRow(i, { start: e.target.value })}
                className="py-2"
                aria-label={t("onb.masters.from")}
              />
              <span className="text-slate-400">–</span>
              <Input
                type="time"
                step={300}
                value={hh(h.end)}
                onChange={(e) => setRow(i, { end: e.target.value })}
                className="py-2"
                aria-label={t("onb.masters.to")}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
